import { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { api } from '@/lib/api';
import { translateError, handleUnauthorizedError } from '@/lib/errorTranslation';
import { useToast } from '@/components/ui/use-toast';
import { LanguageToggle } from '@/components/LanguageToggle';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { ArrowLeft, Syringe, User, Plus, Trash2, Calendar, ArrowUpDown, Bell, Send, AlertTriangle, ClipboardList, Pencil, Scale, Ruler } from 'lucide-react';

interface Patient {
  id: number;
  name: string;
  dob: string;
  sex?: 'male' | 'female' | 'not_specified';
  parent_email: string;
  parent_phone?: string;
  preferred_language: string;
  created_at: string;
}

interface VaccineDose {
  id: number;
  vaccine_id: number;
  vaccine_label: string;
  dose_number: number;
  total_doses: number;
  administered_at: string;
  next_due_at?: string;
  override_next_due_at?: string;
  created_at: string;
}

interface Vaccine {
  id: number;
  label: string;
  total_doses: number;
}

interface UpcomingDose {
  vaccine_id: number;
  vaccine_label: string;
  dose_number: number;
  total_doses: number;
  due_date: string;
  last_dose_date?: string;
  is_overdue: boolean;
}

interface Visit {
  id: number;
  patient_id: number;
  doctor_id: number;
  clinic_id: number;
  visit_date: string;
  weight_kg?: number;
  height_cm?: number;
  doctor_notes?: string;
  parent_notes?: string;
  created_at: string;
  doses?: VaccineDose[];
  custom_vitals?: Array<{ field_name: string; field_value: string; field_unit?: string }>;
}

/**
 * Parse a date string (YYYY-MM-DD) as local time, not UTC.
 * This prevents timezone issues where dates shift by a day.
 */
function parseLocalDate(dateStr: string): Date {
  if (!dateStr) return new Date();
  // If it's already a full ISO datetime (with T) or SQLite datetime (with space), just parse it
  if (dateStr.includes('T') || dateStr.includes(' ')) {
    return new Date(dateStr.replace(' ', 'T'));
  }
  // For date-only strings, parse as local time
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day);
}

export function DoctorPatientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();
  const { toast } = useToast();
  
  const [patient, setPatient] = useState<Patient | null>(null);
  const [doses, setDoses] = useState<VaccineDose[]>([]);
  const [vaccines, setVaccines] = useState<Vaccine[]>([]);
  const [upcomingDoses, setUpcomingDoses] = useState<UpcomingDose[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Active tab state
  const [activeTab, setActiveTab] = useState('info');
  
  // Edit patient modal
  const [showEditForm, setShowEditForm] = useState(false);
  const [editForm, setEditForm] = useState({
    parent_email: '',
    first_name: '',
    last_name: '',
    dob: '',
    sex: 'not_specified' as 'male' | 'female' | 'not_specified',
    vaccines_up_to_date: false,
  });
  
  // Log dose dialog
  const [isLogDoseOpen, setIsLogDoseOpen] = useState(false);
  const [selectedVaccine, setSelectedVaccine] = useState<string>('');
  const [doseNumber, setDoseNumber] = useState<number>(1);
  const [administeredDate, setAdministeredDate] = useState<string>(
    new Date().toISOString().split('T')[0]
  );
  const [savingDose, setSavingDose] = useState(false);
  
  // Delete dose dialog
  const [deletingDose, setDeletingDose] = useState<VaccineDose | null>(null);
  
  // Sort order for vaccine history (newest first by default)
  const [sortNewestFirst, setSortNewestFirst] = useState(true);
  
  // Send reminder state
  const [sendingReminder, setSendingReminder] = useState(false);
  
  // Visits state
  const [visits, setVisits] = useState<Visit[]>([]);
  const [isLogVisitOpen, setIsLogVisitOpen] = useState(false);
  const [visitForm, setVisitForm] = useState({
    visit_date: new Date().toISOString().split('T')[0],
    weight_kg: '',
    height_cm: '',
    doctor_notes: '',
    parent_notes: '',
    doses: [] as Array<{ vaccine_id: number; dose_number: number }>,
  });
  const [savingVisit, setSavingVisit] = useState(false);
  const [deletingVisit, setDeletingVisit] = useState<Visit | null>(null);
  const [editingVisit, setEditingVisit] = useState<Visit | null>(null);
  const [useMetric, setUseMetric] = useState(true);

  useEffect(() => {
    loadData();
  }, [id]);

  // Handle action query param (e.g., ?action=logVisit)
  useEffect(() => {
    const action = searchParams.get('action');
    if (action === 'logVisit' && !loading && patient) {
      openLogVisitDialog();
      // Clear the action param
      setSearchParams({});
    }
  }, [searchParams, loading, patient]);

  const loadData = async () => {
    try {
      if (!id) return;

      setLoading(true);
      
      // Load independently so one transient failure doesn't blank the entire page.
      try {
        const patientResult: any = await api.getPatient(Number(id));
        setPatient(patientResult?.patient || patientResult);
        setUpcomingDoses(patientResult?.upcoming_doses || []);
      } catch (error: any) {
      if (handleUnauthorizedError(error, navigate, 'doctor')) return;
        setPatient(null);
        setUpcomingDoses([]);
        toast({
          title: t('app.error'),
          description: error?.message || t('app.failedToLoadPatient'),
          variant: 'destructive',
        });
      }

      try {
        const dosesResult: any = await api.getVaccineHistory(Number(id));
        setDoses(dosesResult?.doses || []);
        
        // Reload patient data to refresh upcoming doses
        const updatedPatient: any = await api.getPatient(Number(id));
        setUpcomingDoses(updatedPatient?.upcoming_doses || []);
      } catch (error: any) {
      if (handleUnauthorizedError(error, navigate, 'doctor')) return;
        setDoses([]);
        toast({
          title: t('app.error'),
          description: error?.message || t('app.failedToLoadDoses'),
          variant: 'destructive',
        });
      }

      try {
        const vaccinesResult: any = await api.getVaccines();
        setVaccines(vaccinesResult?.vaccines || []);
      } catch (error: any) {
      if (handleUnauthorizedError(error, navigate, 'doctor')) return;
        setVaccines([]);
        toast({
          title: t('app.error'),
          description: error?.message || t('app.failedToLoadVaccines'),
          variant: 'destructive',
        });
      }

      try {
        const visitsResult: any = await api.getPatientVisits(Number(id));
        setVisits(visitsResult?.visits || []);
      } catch (error: any) {
        if (handleUnauthorizedError(error, navigate, 'doctor')) return;
        setVisits([]);
        // Silently fail for visits - not critical
      }
    } catch (error: any) {
      if (handleUnauthorizedError(error, navigate, 'doctor')) return;
      toast({ 
        title: t('app.error'), 
        description: translateError(error, t), 
        variant: 'destructive' 
      });
    } finally {
      setLoading(false);
    }
  };

  // Auto-calculate next dose number when vaccine is selected
  const getNextDoseNumber = (vaccineId: string): number => {
    if (!vaccineId) return 1;
    const vaccineDoses = doses.filter(d => d.vaccine_id === Number(vaccineId));
    if (vaccineDoses.length === 0) return 1;
    const maxDose = Math.max(...vaccineDoses.map(d => d.dose_number));
    return maxDose + 1;
  };

  const handleVaccineChange = (vaccineId: string) => {
    setSelectedVaccine(vaccineId);
    setDoseNumber(getNextDoseNumber(vaccineId));
  };

  const openLogDoseDialog = () => {
    setSelectedVaccine('');
    setDoseNumber(1);
    setAdministeredDate(new Date().toISOString().split('T')[0]);
    setIsLogDoseOpen(true);
  };

  const handleLogDose = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedVaccine || !id) return;
    
    setSavingDose(true);
    try {
      await api.logVaccineDose(Number(id), {
        vaccine_id: Number(selectedVaccine),
        dose_number: doseNumber,
        administered_at: administeredDate,
      });
      
      toast({
        title: t('app.success'),
        description: t('vaccine.logDose'),
      });
      
      setIsLogDoseOpen(false);
      setActiveTab('vaccines'); // Stay on vaccines tab
      loadData();
    } catch (error: any) {
      if (handleUnauthorizedError(error, navigate, 'doctor')) return;
      toast({
        title: t('app.error'),
        description: translateError(error, t),
        variant: 'destructive',
      });
    } finally {
      setSavingDose(false);
    }
  };

  const handleDeleteDose = async () => {
    if (!deletingDose) return;
    
    try {
      await api.deleteVaccineDose(deletingDose.id);
      toast({
        title: t('app.success'),
        description: 'Dose record deleted',
      });
      setDeletingDose(null);
      loadData();
    } catch (error: any) {
      if (handleUnauthorizedError(error, navigate, 'doctor')) return;
      toast({
        title: t('app.error'),
        description: translateError(error, t),
        variant: 'destructive',
      });
    }
  };

  const savePatient = async () => {
    if (!id) return;
    try {
      const payload = {
        parent_email: editForm.parent_email,
        name: `${editForm.first_name} ${editForm.last_name}`.trim(),
        dob: editForm.dob,
        sex: editForm.sex,
        vaccines_up_to_date: editForm.vaccines_up_to_date,
      };
      await api.updatePatient(Number(id), payload);
      toast({ title: t('patient.updated') });
      setShowEditForm(false);
      loadData();
    } catch (error: any) {
      if (handleUnauthorizedError(error, navigate, 'doctor')) return;
      toast({ title: t('app.error'), description: translateError(error, t), variant: 'destructive' });
    }
  };

  const calculateAge = (dob: string): string => {
    const birthDate = parseLocalDate(dob);
    const today = new Date();
    let years = today.getFullYear() - birthDate.getFullYear();
    let months = today.getMonth() - birthDate.getMonth();
    
    if (months < 0) {
      years--;
      months += 12;
    }
    
    if (years > 0) {
      return `${years} year${years > 1 ? 's' : ''}, ${months} month${months === 1 ? '' : 's'}`;
    }
    return `${months} month${months === 1 ? '' : 's'}`;
  };

  // Upcoming doses are loaded from API in loadData()

  const handleSendReminder = async (dose?: UpcomingDose) => {
    if (!patient || !id) return;
    
    setSendingReminder(true);
    try {
      // If specific dose provided, send reminder for just that dose
      // Otherwise send reminder for all upcoming doses
      const dosesToSend = dose ? [dose] : upcomingDoses;
      
      await api.sendVaccineReminder(Number(id), dosesToSend);
      
      toast({
        title: t('app.success'),
        description: dose 
          ? t('vaccine.reminderSent', { doseNumber: dose.dose_number, vaccineLabel: dose.vaccine_label })
          : t('vaccine.reminderSentAll'),
      });
    } catch (error: any) {
      if (handleUnauthorizedError(error, navigate, 'doctor')) return;
      toast({
        title: t('app.error'),
        description: translateError(error, t),
        variant: 'destructive',
      });
    } finally {
      setSendingReminder(false);
    }
  };

  // Visit management functions
  const openLogVisitDialog = () => {
    setVisitForm({
      visit_date: new Date().toISOString().split('T')[0],
      weight_kg: '',
      height_cm: '',
      doctor_notes: '',
      parent_notes: '',
      doses: [],
    });
    setEditingVisit(null);
    setIsLogVisitOpen(true);
  };

  const openEditVisitDialog = (visit: Visit) => {
    setVisitForm({
      visit_date: visit.visit_date?.split('T')[0] || visit.visit_date,
      weight_kg: visit.weight_kg?.toString() || '',
      height_cm: visit.height_cm?.toString() || '',
      doctor_notes: visit.doctor_notes || '',
      parent_notes: visit.parent_notes || '',
      doses: [],
    });
    setEditingVisit(visit);
    setIsLogVisitOpen(true);
  };

  const handleLogVisit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;

    setSavingVisit(true);
    try {
      const data: any = {
        patient_id: Number(id),
        visit_date: visitForm.visit_date,
      };

      if (visitForm.weight_kg) {
        // Convert from lb to kg if using imperial
        data.weight_kg = useMetric
          ? Number.parseFloat(visitForm.weight_kg)
          : Number.parseFloat(visitForm.weight_kg) * 0.453592;
      }

      if (visitForm.height_cm) {
        // Convert from inches to cm if using imperial
        data.height_cm = useMetric
          ? Number.parseFloat(visitForm.height_cm)
          : Number.parseFloat(visitForm.height_cm) * 2.54;
      }

      if (visitForm.doctor_notes) {
        data.doctor_notes = visitForm.doctor_notes;
      }

      if (visitForm.parent_notes) {
        data.parent_notes = visitForm.parent_notes;
      }

      if (visitForm.doses.length > 0) {
        data.doses = visitForm.doses;
      }

      if (editingVisit) {
        await api.updateVisit(editingVisit.id, data);
        toast({
          title: t('app.success'),
          description: t('visit.visitUpdated'),
        });
      } else {
        await api.createVisit(data);
        toast({
          title: t('app.success'),
          description: t('visit.visitCreated'),
        });
      }

      setIsLogVisitOpen(false);
      setEditingVisit(null);
      loadData();
    } catch (error: any) {
      if (handleUnauthorizedError(error, navigate, 'doctor')) return;
      toast({
        title: t('app.error'),
        description: translateError(error, t),
        variant: 'destructive',
      });
    } finally {
      setSavingVisit(false);
    }
  };

  const handleDeleteVisit = async () => {
    if (!deletingVisit) return;

    try {
      await api.deleteVisit(deletingVisit.id);
      toast({
        title: t('app.success'),
        description: t('visit.visitDeleted'),
      });
      setDeletingVisit(null);
      loadData();
    } catch (error: any) {
      if (handleUnauthorizedError(error, navigate, 'doctor')) return;
      toast({
        title: t('app.error'),
        description: translateError(error, t),
        variant: 'destructive',
      });
    }
  };

  const addDoseToVisit = () => {
    if (!selectedVaccine) return;
    const nextDose = getNextDoseNumber(selectedVaccine);
    setVisitForm({
      ...visitForm,
      doses: [
        ...visitForm.doses,
        { vaccine_id: Number(selectedVaccine), dose_number: nextDose },
      ],
    });
    setSelectedVaccine('');
  };

  const removeDoseFromVisit = (index: number) => {
    setVisitForm({
      ...visitForm,
      doses: visitForm.doses.filter((_, i) => i !== index),
    });
  };

  // Unit conversion helpers
  const kgToLb = (kg: number) => (kg * 2.20462).toFixed(1);
  const cmToIn = (cm: number) => (cm / 2.54).toFixed(1);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>{t('app.loading')}</p>
      </div>
    );
  }

  if (!patient) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>{t('patient.notFound')}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/doctor/dashboard')} title={t('nav.patients')} className="touch-action-manipulation min-h-[44px] min-w-[44px]">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <button onClick={() => navigate('/doctor/dashboard')} className="flex items-center gap-2 hover:opacity-80 transition-opacity">
              <img src="https://pub-327c49603f8c4373ac8b69237691fafc.r2.dev/rds-logo.png" alt="Dashboard" className="h-10 w-10 sm:h-12 sm:w-12" />
            </button>
            <h1 className="text-lg sm:text-2xl font-bold text-gray-900 truncate max-w-[200px] sm:max-w-none">🧒 {patient.name}</h1>
          </div>
          <LanguageToggle />
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-2 sm:grid-cols-4 lg:w-[800px] gap-1">
            <TabsTrigger value="info" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm touch-action-manipulation min-h-[44px]">
              <User className="h-4 w-4" />
              <span className="hidden sm:inline">{t('patient.personalInfo')}</span>
              <span className="sm:hidden">Info</span>
            </TabsTrigger>
            <TabsTrigger value="visits" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm touch-action-manipulation min-h-[44px]">
              <ClipboardList className="h-4 w-4" />
              <span className="hidden sm:inline">{t('visit.visits')}</span>
              <span className="sm:hidden">{t('visit.visits')}</span>
              {visits.length > 0 && (
                <span className="ml-1 bg-green-500 text-white text-xs rounded-full px-2 py-0.5">
                  {visits.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="vaccines" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm touch-action-manipulation min-h-[44px]">
              <Syringe className="h-4 w-4" />
              <span className="hidden sm:inline">{t('vaccine.vaccines')}</span>
              <span className="sm:hidden">{t('vaccine.vaccines')}</span>
            </TabsTrigger>
            <TabsTrigger value="upcoming" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm touch-action-manipulation min-h-[44px]">
              <Bell className="h-4 w-4" />
              <span className="hidden sm:inline">{t('vaccine.upcoming')}</span>
              <span className="sm:hidden">{t('vaccine.upcoming')}</span>
              {upcomingDoses.length > 0 && (
                <span className="ml-1 bg-blue-500 text-white text-xs rounded-full px-2 py-0.5">
                  {upcomingDoses.length}
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Personal Info Tab */}
          <TabsContent value="info">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="h-5 w-5" />
                  {t('patient.patientInfo')}
                </CardTitle>
                <CardDescription>
                  {t('patient.personalInfoDesc')}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium text-gray-500">{t('patient.name')}</Label>
                    <p className="text-lg">{patient.name}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-gray-500">{t('patient.dob')}</Label>
                    <p className="text-lg">{parseLocalDate(patient.dob).toLocaleDateString()}</p>
                    <p className="text-sm text-gray-500">{t('patient.age')}: {calculateAge(patient.dob)}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-gray-500">{t('patient.sex')}</Label>
                    <p className="text-lg">
                      {(() => {
                        if (patient.sex === 'male') return t('patient.sexMale');
                        if (patient.sex === 'female') return t('patient.sexFemale');
                        return t('patient.sexNotSpecified');
                      })()}
                    </p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-gray-500">{t('patient.parentEmail')}</Label>
                    <p className="text-lg">{patient.parent_email}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-gray-500">{t('patient.phone')}</Label>
                    <p className="text-lg">{patient.parent_phone || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-gray-500">{t('patient.language')}</Label>
                    <p className="text-lg">{patient.preferred_language === 'es' ? 'Español' : 'English'}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-gray-500">{t('patient.created')}</Label>
                    <p className="text-lg">
                      {patient.created_at ? parseLocalDate(patient.created_at).toLocaleDateString() : '-'}
                    </p>
                  </div>
                </div>
                
                <div className="pt-4 flex gap-2">
                  <Button variant="outline" onClick={() => {
                    setEditForm({
                      parent_email: patient.parent_email || '',
                      first_name: patient.name?.split(' ')[0] || '',
                      last_name: patient.name?.split(' ').slice(1).join(' ') || '',
                      dob: patient.dob || '',
                      sex: patient.sex || 'not_specified',
                      vaccines_up_to_date: false,
                    });
                    setShowEditForm(true);
                  }}>
                    {t('patient.editPatient')}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Visits Tab */}
          <TabsContent value="visits">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <ClipboardList className="h-5 w-5" />
                      {t('visit.visits')}
                    </CardTitle>
                    <CardDescription>
                      {t('vaccine.historyDesc')}
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={openLogVisitDialog}>
                      <Plus className="h-4 w-4 mr-2" />
                      {t('visit.logVisit')}
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {visits.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <ClipboardList className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>{t('visit.noVisits')}</p>
                    <p className="text-sm">{t('visit.noVisitsDesc')}</p>
                    <Button className="mt-4" onClick={openLogVisitDialog}>
                      <Plus className="h-4 w-4 mr-2" />
                      {t('visit.logVisit')}
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {visits
                      .sort((a, b) => new Date(b.visit_date).getTime() - new Date(a.visit_date).getTime())
                      .map((visit) => (
                        <div
                          key={visit.id}
                          className="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
                        >
                          <div className="flex justify-between items-start mb-3">
                            <div className="flex items-center gap-3">
                              <div className="bg-green-100 p-2 rounded-full">
                                <Calendar className="h-5 w-5 text-green-600" />
                              </div>
                              <div>
                                <p className="font-medium">
                                  {parseLocalDate(visit.visit_date).toLocaleDateString()}
                                </p>
                                <p className="text-sm text-gray-500">
                                  {t('visit.visitDate')}
                                </p>
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => openEditVisitDialog(visit)}
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="destructive"
                                size="sm"
                                onClick={() => setDeletingVisit(visit)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>

                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            {visit.weight_kg && (
                              <div className="flex items-center gap-2">
                                <Scale className="h-4 w-4 text-gray-400" />
                                <div>
                                  <p className="text-sm text-gray-500">{t('visit.weight')}</p>
                                  <p className="font-medium">
                                    {useMetric
                                      ? `${visit.weight_kg.toFixed(1)} kg`
                                      : `${kgToLb(visit.weight_kg)} lb`}
                                  </p>
                                </div>
                              </div>
                            )}
                            {visit.height_cm && (
                              <div className="flex items-center gap-2">
                                <Ruler className="h-4 w-4 text-gray-400" />
                                <div>
                                  <p className="text-sm text-gray-500">{t('visit.height')}</p>
                                  <p className="font-medium">
                                    {useMetric
                                      ? `${visit.height_cm.toFixed(1)} cm`
                                      : `${cmToIn(visit.height_cm)} in`}
                                  </p>
                                </div>
                              </div>
                            )}
                            {visit.doses && visit.doses.length > 0 && (
                              <div className="col-span-2">
                                <p className="text-sm text-gray-500">{t('visit.vaccinesAdministered')}</p>
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {visit.doses.map((dose, idx) => (
                                    <span
                                      key={idx}
                                      className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded"
                                    >
                                      {dose.vaccine_label} (#{dose.dose_number})
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>

                          {(visit.doctor_notes || visit.parent_notes) && (
                            <div className="mt-3 pt-3 border-t">
                              {visit.doctor_notes && (
                                <div className="mb-2">
                                  <p className="text-sm text-gray-500">{t('visit.doctorNotes')}</p>
                                  <p className="text-sm">{visit.doctor_notes}</p>
                                </div>
                              )}
                              {visit.parent_notes && (
                                <div>
                                  <p className="text-sm text-gray-500">{t('visit.parentNotes')}</p>
                                  <p className="text-sm">{visit.parent_notes}</p>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Vaccines Tab */}
          <TabsContent value="vaccines">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Syringe className="h-5 w-5" />
                      {t('vaccine.history')}
                    </CardTitle>
                    <CardDescription>
                      {t('vaccine.historyDesc')}
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setSortNewestFirst(!sortNewestFirst)}
                      className="flex items-center gap-1"
                    >
                      <ArrowUpDown className="h-4 w-4" />
                      {sortNewestFirst ? t('common.newestFirst') : t('common.oldestFirst')}
                    </Button>
                    <Button onClick={openLogDoseDialog}>
                      <Plus className="h-4 w-4 mr-2" />
                      {t('vaccine.logDose')}
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {doses.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Syringe className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>{t('vaccine.noDosesRecorded')}</p>
                    <Button className="mt-4" onClick={openLogDoseDialog}>
                      <Plus className="h-4 w-4 mr-2" />
                      {t('vaccine.logDose')}
                    </Button>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t('vaccine.vaccineName')}</TableHead>
                        <TableHead>{t('vaccine.doseNumber')}</TableHead>
                        <TableHead>{t('vaccine.administeredDate')}</TableHead>
                        <TableHead>{t('vaccine.status')}</TableHead>
                        <TableHead className="text-right">{t('common.actions')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {[...doses]
                        .sort((a, b) => {
                          const dateA = parseLocalDate(a.administered_at).getTime();
                          const dateB = parseLocalDate(b.administered_at).getTime();
                          return sortNewestFirst ? dateB - dateA : dateA - dateB;
                        })
                        .map((dose) => {
                        const isComplete = dose.dose_number >= dose.total_doses;
                        return (
                        <TableRow key={dose.id}>
                          <TableCell className="font-medium">{dose.vaccine_label}</TableCell>
                          <TableCell>{t('vaccine.doseOf', { current: dose.dose_number, total: dose.total_doses })}</TableCell>
                          <TableCell>
                            {parseLocalDate(dose.administered_at).toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            {isComplete ? (
                              <span className="text-green-600 font-medium">✓ {t('vaccine.complete')}</span>
                            ) : (
                              <span className="text-blue-600">✓ {t('vaccine.recorded')}</span>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => setDeletingDose(dose)}
                            >
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </TableCell>
                        </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Upcoming Vaccines Tab */}
          <TabsContent value="upcoming">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Bell className="h-5 w-5" />
                      {t('vaccine.upcomingVaccines')}
                    </CardTitle>
                    <CardDescription>
                      {t('vaccine.upcomingDesc')}
                    </CardDescription>
                  </div>
                  {upcomingDoses.length > 0 && (
                    <Button onClick={() => handleSendReminder()} disabled={sendingReminder}>
                      <Send className="h-4 w-4 mr-2" />
                      {sendingReminder ? t('vaccine.sending') : t('vaccine.sendReminderAll')}
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {upcomingDoses.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Bell className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p className="text-lg font-medium">{t('patient.vaccinesUpToDate')}</p>
                    <p className="text-sm mt-2">{t('vaccine.noPendingDoses')}</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t('vaccine.vaccineName')}</TableHead>
                        <TableHead>{t('vaccine.doseNumber')}</TableHead>
                        <TableHead>{t('vaccine.administeredDate')}</TableHead>
                        <TableHead>{t('vaccine.nextDueDate')}</TableHead>
                        <TableHead>{t('vaccine.status')}</TableHead>
                        <TableHead className="text-right">{t('common.actions')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {upcomingDoses.map((dose) => {
                        const dueDate = parseLocalDate(dose.due_date);
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        const daysUntilDue = Math.ceil((dueDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
                        
                        return (
                          <TableRow key={`${dose.vaccine_id}-${dose.dose_number}`} className={dose.is_overdue ? 'bg-red-50' : ''}>
                            <TableCell className="font-medium">{dose.vaccine_label}</TableCell>
                            <TableCell>
                              {t('vaccine.doseOf', { current: dose.dose_number, total: dose.total_doses })}
                            </TableCell>
                            <TableCell>
                              {dose.last_dose_date ? parseLocalDate(dose.last_dose_date).toLocaleDateString() : t('vaccine.firstDose')}
                            </TableCell>
                            <TableCell>
                              <span className="flex items-center gap-1">
                                <Calendar className="h-4 w-4" />
                                {dueDate.toLocaleDateString()}
                              </span>
                            </TableCell>
                            <TableCell>
                              {dose.is_overdue ? (
                                <span className="text-red-600 font-medium flex items-center gap-1">
                                  <AlertTriangle className="h-4 w-4" />
                                  {t('vaccine.daysOverdue', { days: Math.abs(daysUntilDue) })}
                                </span>
                              ) : daysUntilDue <= 7 ? (
                                <span className="text-yellow-600 font-medium">
                                  {t('vaccine.dueInDays', { days: daysUntilDue })}
                                </span>
                              ) : (
                                <span className="text-blue-600">
                                  {t('vaccine.inDays', { days: daysUntilDue })}
                                </span>
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleSendReminder(dose)}
                                disabled={sendingReminder}
                              >
                                <Send className="h-4 w-4 mr-1" />
                                {t('vaccine.sendReminder')}
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Log Dose Dialog */}
      <Dialog open={isLogDoseOpen} onOpenChange={setIsLogDoseOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('vaccine.logDose')}</DialogTitle>
            <DialogDescription>
              {t('vaccine.recordDoseFor')} {patient.name}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleLogDose}>
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="vaccine">{t('vaccine.vaccineName')} *</Label>
                <Select value={selectedVaccine} onValueChange={handleVaccineChange}>
                  <SelectTrigger>
                    <SelectValue placeholder={t('vaccine.selectVaccine')} />
                  </SelectTrigger>
                  <SelectContent>
                    {vaccines.map((vaccine) => {
                      const administeredDoses = doses.filter(d => d.vaccine_id === vaccine.id).length;
                      const isComplete = administeredDoses >= vaccine.total_doses;
                      return (
                        <SelectItem 
                          key={vaccine.id} 
                          value={vaccine.id.toString()}
                          disabled={isComplete}
                        >
                          {vaccine.label} ({administeredDoses}/{vaccine.total_doses} doses)
                          {isComplete && ' ✓'}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="dose_number">{t('vaccine.doseNumber')} *</Label>
                <Input
                  id="dose_number"
                  type="number"
                  min="1"
                  max={selectedVaccine ? vaccines.find(v => v.id.toString() === selectedVaccine)?.total_doses : undefined}
                  value={doseNumber}
                  onChange={(e) => setDoseNumber(Number.parseInt(e.target.value, 10))}
                  required
                />
                {selectedVaccine && (
                  <p className="text-sm text-gray-500 mt-1">
                    {t('vaccine.maxDoses')}: {vaccines.find(v => v.id.toString() === selectedVaccine)?.total_doses} {t('vaccine.doses')}
                  </p>
                )}
              </div>
              <div>
                <Label htmlFor="administered_at">{t('vaccine.administeredDate')} *</Label>
                <Input
                  id="administered_at"
                  type="date"
                  value={administeredDate}
                  onChange={(e) => setAdministeredDate(e.target.value)}
                  required
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsLogDoseOpen(false)}>
                {t('common.cancel')}
              </Button>
              <Button type="submit" disabled={savingDose || !selectedVaccine}>
                {savingDose ? t('app.loading') : t('common.save')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Dose Confirmation */}
      <AlertDialog open={!!deletingDose} onOpenChange={() => setDeletingDose(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('vaccine.deleteDoseTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('vaccine.deleteDoseDesc')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteDose} className="bg-red-500 hover:bg-red-600">
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit Patient Modal */}
      {showEditForm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-lg p-6">
            <h3 className="text-lg font-semibold mb-4">{t('patient.edit')}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="first">{t('patient.firstName')}</Label>
                <Input id="first" value={editForm.first_name} onChange={(e) => setEditForm({ ...editForm, first_name: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="last">{t('patient.lastName')}</Label>
                <Input id="last" value={editForm.last_name} onChange={(e) => setEditForm({ ...editForm, last_name: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="email">{t('auth.email')}</Label>
                <Input id="email" type="email" value={editForm.parent_email} onChange={(e) => setEditForm({ ...editForm, parent_email: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="dob">{t('patient.dob')}</Label>
                <Input id="dob" type="date" value={editForm.dob} onChange={(e) => setEditForm({ ...editForm, dob: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="sex">{t('patient.sex')}</Label>
                <Select value={editForm.sex} onValueChange={(value: 'male' | 'female' | 'not_specified') => setEditForm({ ...editForm, sex: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder={t('patient.sexNotSpecified')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">{t('patient.sexMale')}</SelectItem>
                    <SelectItem value="female">{t('patient.sexFemale')}</SelectItem>
                    <SelectItem value="not_specified">{t('patient.sexNotSpecified')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowEditForm(false)}>{t('common.cancel')}</Button>
              <Button onClick={savePatient}>{t('common.save')}</Button>
            </div>
          </div>
        </div>
      )}

      {/* Log Visit Dialog */}
      <Dialog open={isLogVisitOpen} onOpenChange={setIsLogVisitOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>
              {editingVisit ? t('visit.editVisit') : t('visit.newVisit')}
            </DialogTitle>
            <DialogDescription>
              {patient?.name}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleLogVisit}>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="visit_date">{t('visit.visitDate')}</Label>
                  <Input
                    id="visit_date"
                    type="date"
                    value={visitForm.visit_date}
                    onChange={(e) => setVisitForm({ ...visitForm, visit_date: e.target.value })}
                    required
                  />
                </div>
                <div className="flex items-end gap-2">
                  <Button
                    type="button"
                    variant={useMetric ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setUseMetric(true)}
                  >
                    kg/cm
                  </Button>
                  <Button
                    type="button"
                    variant={!useMetric ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setUseMetric(false)}
                  >
                    lb/in
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="weight">
                    {useMetric ? t('visit.weightKg') : t('visit.weightLb')}
                  </Label>
                  <Input
                    id="weight"
                    type="number"
                    step="0.1"
                    placeholder={useMetric ? 'kg' : 'lb'}
                    value={visitForm.weight_kg}
                    onChange={(e) => setVisitForm({ ...visitForm, weight_kg: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="height">
                    {useMetric ? t('visit.heightCm') : t('visit.heightIn')}
                  </Label>
                  <Input
                    id="height"
                    type="number"
                    step="0.1"
                    placeholder={useMetric ? 'cm' : 'in'}
                    value={visitForm.height_cm}
                    onChange={(e) => setVisitForm({ ...visitForm, height_cm: e.target.value })}
                  />
                </div>
              </div>

              {/* Vaccine doses to log during this visit */}
              {!editingVisit && (
                <div className="border rounded-lg p-4">
                  <Label className="mb-2 block">{t('visit.vaccinesAdministered')}</Label>
                  
                  {visitForm.doses.length > 0 && (
                    <div className="space-y-2 mb-3">
                      {visitForm.doses.map((dose, idx) => {
                        const vaccine = vaccines.find(v => v.id === dose.vaccine_id);
                        return (
                          <div key={idx} className="flex items-center justify-between bg-blue-50 p-2 rounded">
                            <span className="text-sm">
                              {vaccine?.label} - {t('vaccine.doseNumber')} {dose.dose_number}
                            </span>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeDoseFromVisit(idx)}
                            >
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  <div className="flex gap-2">
                    <Select value={selectedVaccine} onValueChange={setSelectedVaccine}>
                      <SelectTrigger className="flex-1">
                        <SelectValue placeholder={t('vaccine.selectVaccine')} />
                      </SelectTrigger>
                      <SelectContent>
                        {vaccines.map((v) => (
                          <SelectItem key={v.id} value={v.id.toString()}>
                            {v.label} ({t('vaccine.maxDoses')}: {v.total_doses})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={addDoseToVisit}
                      disabled={!selectedVaccine}
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}

              <div>
                <Label htmlFor="doctor_notes">{t('visit.doctorNotes')}</Label>
                <textarea
                  id="doctor_notes"
                  className="w-full min-h-[80px] p-2 border rounded-md"
                  placeholder={t('visit.optionalNotes')}
                  value={visitForm.doctor_notes}
                  onChange={(e) => setVisitForm({ ...visitForm, doctor_notes: e.target.value })}
                />
              </div>

              <div>
                <Label htmlFor="parent_notes">{t('visit.parentNotes')}</Label>
                <textarea
                  id="parent_notes"
                  className="w-full min-h-[80px] p-2 border rounded-md"
                  placeholder={t('visit.optionalNotes')}
                  value={visitForm.parent_notes}
                  onChange={(e) => setVisitForm({ ...visitForm, parent_notes: e.target.value })}
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsLogVisitOpen(false)}>
                {t('common.cancel')}
              </Button>
              <Button type="submit" disabled={savingVisit}>
                {savingVisit ? t('app.loading') : t('common.save')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Visit Confirm Dialog */}
      <AlertDialog open={!!deletingVisit} onOpenChange={(open) => !open && setDeletingVisit(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('visit.deleteVisit')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('visit.confirmDeleteVisit')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteVisit} className="bg-red-500 hover:bg-red-600">
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}